import React, { useState, useEffect } from "react";
import { apiGet, apiPost } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
import "./FollowupHome.css";

interface FollowupItem {
    id: number;
    patient_id: string;
    patient_name?: string;
    mobile?: string;
    procedure_type: string;
    tooth_no?: string;
    followup_stage: string;
    scheduled_date: string;
    status: string;
    pain_score?: number;
    swelling_score?: number;
    bleeding_score?: number;
    complaint?: string;
    staff_notes?: string;
    doctor_notes?: string;
}

interface FollowupQuestion {
    id: number;
    question_text: string;
    answer_type: string;
    [key: string]: unknown;
}

const KANBAN_COLUMNS = [
    { id: 'SCHEDULED', title: 'To Do (Scheduled)' },
    { id: 'CALL_PENDING', title: 'In Progress (Call Pending)' },
    { id: 'RESCHEDULED', title: 'Rescheduled' },
    { id: 'COMPLETED', title: 'Done (Completed)' }
];

export default function FollowupHome() {
    const { showToast } = useToast();
    const [items, setItems] = useState<FollowupItem[]>([]);
    const [loading, setLoading] = useState(false);
    
    // Kanban states
    const [searchQuery, setSearchQuery] = useState("");
    const [mineOnly, setMineOnly] = useState(false);
    const [sortDesc, setSortDesc] = useState(false); // false = asc, true = desc
    const [draggedItemId, setDraggedItemId] = useState<number | null>(null);

    // Modal states
    const [selectedItem, setSelectedItem] = useState<FollowupItem | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [questions, setQuestions] = useState<FollowupQuestion[]>([]);
    const [answers, setAnswers] = useState<Record<number, string>>({});
    const [callStatus, setCallStatus] = useState<string>("CALLED");
    const [callNotes, setCallNotes] = useState<string>("");
    const [savingQa, setSavingQa] = useState(false);

    const loadFollowups = async () => {
        setLoading(true);
        try {
            const data = await apiGet<FollowupItem[]>("/api/followups/pending");
            setItems(Array.isArray(data) ? data : []);
        } catch (err: any) {
            showToast({ message: `Failed to load followups: ${err.message}`, type: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFollowups();
        const interval = setInterval(loadFollowups, 60000);
        return () => clearInterval(interval);
    }, []);

    // Drag and Drop Handlers
    const handleDragStart = (e: React.DragEvent, id: number) => {
        setDraggedItemId(id);
        e.dataTransfer.effectAllowed = 'move';
        // HTML5 requires setting data, even if unused directly
        e.dataTransfer.setData('text/plain', id.toString());
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault(); // necessary to allow dropping
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e: React.DragEvent, targetStatus: string) => {
        e.preventDefault();
        if (!draggedItemId) return;
        
        const itemToUpdate = items.find(i => i.id === draggedItemId);
        if (!itemToUpdate || itemToUpdate.status === targetStatus) {
            setDraggedItemId(null);
            return;
        }

        // Optimistic update
        setItems(prev => prev.map(i => i.id === draggedItemId ? { ...i, status: targetStatus } : i));
        
        try {
            await apiPost("/api/followups/update", {
                id: itemToUpdate.id,
                status: targetStatus,
                pain_score: itemToUpdate.pain_score,
                swelling_score: itemToUpdate.swelling_score,
                bleeding_score: itemToUpdate.bleeding_score,
                staff_notes: itemToUpdate.staff_notes,
                doctor_notes: itemToUpdate.doctor_notes,
                actual_date: new Date().toISOString().slice(0, 10)
            });
            showToast({ message: "Task moved successfully", type: "success" });
        } catch (err: any) {
            // Revert on failure
            setItems(prev => prev.map(i => i.id === draggedItemId ? { ...i, status: itemToUpdate.status } : i));
            showToast({ message: `Failed to move task: ${err.message}`, type: "error" });
        } finally {
            setDraggedItemId(null);
        }
    };

    // Card UI Interactions
    const handleUpdateClick = (item: FollowupItem) => {
        setSelectedItem({ ...item });
        setShowModal(true);
        setQuestions([]);
        setAnswers({});
        setCallStatus("CALLED");
        setCallNotes("");
    };

    useEffect(() => {
        const run = async () => {
            if (!showModal || !selectedItem?.id) return;
            try {
                const data = await apiGet<FollowupQuestion[]>(`/api/followups/${selectedItem.id}/questions`);
                setQuestions(Array.isArray(data) ? data : []);
            } catch {
                setQuestions([]);
            }
        };
        void run();
    }, [showModal, selectedItem?.id]);

    const handleSaveUpdate = async () => {
        if (!selectedItem) return;

        try {
            await apiPost("/api/followups/update", {
                id: selectedItem.id,
                status: selectedItem.status,
                pain_score: selectedItem.pain_score,
                swelling_score: selectedItem.swelling_score,
                bleeding_score: selectedItem.bleeding_score,
                staff_notes: selectedItem.staff_notes,
                doctor_notes: selectedItem.doctor_notes,
                actual_date: new Date().toISOString().slice(0, 10)
            });

            showToast({ message: "Followup updated successfully", type: "success" });
            setShowModal(false);
            setSelectedItem(null);
            loadFollowups();
        } catch (err: any) {
            showToast({ message: `Failed to update: ${err.message}`, type: "error" });
        }
    };

    const handleCompleteFollowup = async () => {
        if (!selectedItem?.id) return;
        setSavingQa(true);
        try {
            await apiPost(`/api/followups/${selectedItem.id}/call`, {
                call_status: callStatus,
                call_notes: callNotes || null,
            });

            const cs = String(callStatus || "").toUpperCase();
            if (cs === "NO_ANSWER" || cs === "PATIENT_REFUSED") {
                showToast({ message: `Call logged (${cs}).`, type: "success" });
                setShowModal(false);
                setSelectedItem(null);
                loadFollowups();
                return;
            }

            const payloadAnswers = Object.entries(answers)
                .map(([qid, val]) => ({ question_id: Number(qid), answer_value: String(val ?? "") }))
                .filter((a) => a.question_id && a.answer_value.trim() !== "");

            await apiPost(`/api/followups/${selectedItem.id}/answers`, { answers: payloadAnswers });

            showToast({ message: "Followup completed", type: "success" });
            setShowModal(false);
            setSelectedItem(null);
            loadFollowups();
        } catch (err: any) {
            showToast({ message: `Failed to complete followup: ${err.message}`, type: "error" });
        } finally {
            setSavingQa(false);
        }
    };

    // Filter and Sort Logic
    // Apply search filter and "mine only" filter (mocking it by searching text for simplicity if no user session is bound)
    let filteredItems = items.filter(i => {
        if (searchQuery && !i.patient_name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        // Mock mine only logic: normally checks if i.assigned_to === currentUserId
        if (mineOnly && !i.doctor_notes?.toLowerCase().includes("mine")) return false;
        return true;
    });

    filteredItems.sort((a, b) => {
        const da = new Date(a.scheduled_date).getTime();
        const db = new Date(b.scheduled_date).getTime();
        return sortDesc ? db - da : da - db;
    });

    const isOverdue = (dateStr: string) => {
        return new Date(dateStr) < new Date(new Date().setHours(0,0,0,0));
    };

    return (
        <div className="apexo-kanban-workspace">
            <div className="apexo-kanban-header">
                <div>
                    <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.25rem', color: '#0f172a' }}>📞 Kanban Followups</h2>
                    <p className="mod-text-muted" style={{ margin: '4px 0 0 0', fontSize: '0.9rem' }}>Drag and drop to progress through stages.</p>
                </div>
                <div className="apexo-kanban-controls">
                    <input 
                        type="text" 
                        placeholder="Search patient..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="apexo-kanban-search"
                    />
                    
                    <button 
                        className={`apexo-filter-toggle ${mineOnly ? 'active' : ''}`}
                        onClick={() => setMineOnly(!mineOnly)}
                    >
                        🙋‍♂️ Mine Only
                    </button>

                    <button 
                        className="apexo-filter-toggle"
                        onClick={() => setSortDesc(!sortDesc)}
                        title="Toggle Sort Direction"
                    >
                        {sortDesc ? '⬇️ Desc' : '⬆️ Asc'}
                    </button>

                    <Button variant="outline" onClick={loadFollowups} isLoading={loading}>🔄 Refresh</Button>
                </div>
            </div>

            <div className="apexo-kanban-board">
                {KANBAN_COLUMNS.map(col => {
                    const colItems = filteredItems.filter(i => {
                        // Map MISSED into SCHEDULED column if you want, or render dynamically.
                        // APEXO style allows dynamic maps, we map MISSED to the first column too just so it doesn't vanish
                        if (col.id === 'SCHEDULED' && i.status === 'MISSED') return true;
                        return i.status === col.id;
                    });
                    
                    return (
                        <div 
                            key={col.id} 
                            className="apexo-kanban-column"
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, col.id)}
                        >
                            <div className="apexo-kanban-col-header">
                                <h3>{col.title}</h3>
                                <span className="apexo-col-badge">{colItems.length}</span>
                            </div>
                            
                            <div className="apexo-kanban-col-body">
                                {colItems.map(item => (
                                    <div 
                                        key={item.id} 
                                        className={`apexo-kanban-card ${isOverdue(item.scheduled_date) && item.status !== 'COMPLETED' ? 'overdue' : ''}`}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, item.id)}
                                        onDoubleClick={() => handleUpdateClick(item)}
                                    >
                                        <div className="apexo-card-header">
                                            <span className="apexo-card-patient">{item.patient_name || 'Unknown'}</span>
                                            {isOverdue(item.scheduled_date) && item.status !== 'COMPLETED' && <span className="apexo-tag-urgent">Overdue</span>}
                                        </div>
                                        <div className="apexo-card-meta">
                                            <span>📱 {item.mobile}</span>
                                            <span>🗓️ {new Date(item.scheduled_date).toLocaleDateString("en-GB")}</span>
                                        </div>
                                        <div className="apexo-card-procedure">
                                            {item.procedure_type} {item.tooth_no ? `(Tooth #${item.tooth_no})` : ''}
                                        </div>
                                        <div className="apexo-card-stage">{item.followup_stage}</div>
                                        
                                        <div className="apexo-card-actions">
                                            <button className="apexo-action-icon-btn" onClick={() => handleUpdateClick(item)}>
                                                ✏️ Update
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {colItems.length === 0 && (
                                    <div className="apexo-empty-col">No items</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* APEXO Style Update Modal */}
            {showModal && selectedItem && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowModal(false)}>
                    <div style={{ width: '600px', maxWidth: '95vw', background: '#fff', padding: '24px', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                        
                        <div style={{ flexShrink: 0, borderBottom: '1px solid #e2e8f0', paddingBottom: '16px', marginBottom: '16px' }}>
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.25rem', color: '#0f172a' }}>Record Follow-up</h3>
                            <p style={{ margin: 0, color: '#64748b', fontSize: '0.95rem' }}>
                                {selectedItem.patient_name} • {selectedItem.procedure_type} • Due: {new Date(selectedItem.scheduled_date).toLocaleDateString("en-GB")}
                            </p>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px', paddingRight: '8px' }}>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Followup Status</label>
                                <select value={selectedItem.status} onChange={(e) => setSelectedItem({ ...selectedItem, status: e.target.value })} className="apexo-modal-input">
                                    <option value="SCHEDULED">Scheduled</option>
                                    <option value="CALL_PENDING">Call Pending</option>
                                    <option value="COMPLETED">Completed</option>
                                    <option value="MISSED">Missed</option>
                                    <option value="RESCHEDULED">Rescheduled</option>
                                </select>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Pain (0-10)</label>
                                    <input type="number" min="0" max="10" value={selectedItem.pain_score || 0} onChange={(e) => setSelectedItem({ ...selectedItem, pain_score: parseInt(e.target.value) })} className="apexo-modal-input" />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Swelling</label>
                                    <input type="number" min="0" max="10" value={selectedItem.swelling_score || 0} onChange={(e) => setSelectedItem({ ...selectedItem, swelling_score: parseInt(e.target.value) })} className="apexo-modal-input" />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Bleeding</label>
                                    <input type="number" min="0" max="10" value={selectedItem.bleeding_score || 0} onChange={(e) => setSelectedItem({ ...selectedItem, bleeding_score: parseInt(e.target.value) })} className="apexo-modal-input" />
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Staff Notes</label>
                                <textarea value={selectedItem.staff_notes || ""} onChange={(e) => setSelectedItem({ ...selectedItem, staff_notes: e.target.value })} placeholder="Add notes..." className="apexo-modal-input" style={{ minHeight: '60px', resize: 'vertical' }} />
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Doctor's Notes</label>
                                <textarea value={selectedItem.doctor_notes || ""} onChange={e => setSelectedItem({...selectedItem, doctor_notes: e.target.value})} placeholder="Add clinical remarks..." className="apexo-modal-input" style={{ minHeight: '60px', resize: 'vertical' }} />
                            </div>

                            {/* Questionnaire Section */}
                            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', color: '#64748b', textTransform: 'uppercase' }}>📞 Call Action</h4>
                                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>Call Status</label>
                                        <select value={callStatus} onChange={(e) => setCallStatus(e.target.value)} className="apexo-modal-input">
                                            <option value="CALLED">Reached</option>
                                            <option value="NO_ANSWER">No Answer</option>
                                            <option value="PATIENT_REFUSED">Refused / Callback Requested</option>
                                        </select>
                                    </div>
                                    <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>Call Remarks</label>
                                        <input value={callNotes} onChange={(e) => setCallNotes(e.target.value)} placeholder="Short note..." className="apexo-modal-input" />
                                    </div>
                                </div>

                                {questions.length > 0 && (
                                    <>
                                        <h4 style={{ margin: '16px 0 12px 0', fontSize: '0.9rem', color: '#64748b', textTransform: 'uppercase' }}>Assessment</h4>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {questions.map((q) => {
                                                const t = String(q.answer_type || "").toUpperCase();
                                                const val = answers[q.id] ?? "";
                                                return (
                                                    <div key={q.id} style={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: '12px', alignItems: 'center' }}>
                                                        <span style={{ fontSize: '0.9rem', color: '#334155' }}>{q.question_text}</span>
                                                        {t === "YES_NO" ? (
                                                            <select value={val} onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))} className="apexo-modal-input">
                                                                <option value="">--</option>
                                                                <option value="YES">Yes</option>
                                                                <option value="NO">No</option>
                                                            </select>
                                                        ) : t === "SCALE_0_10" ? (
                                                            <input type="number" min={0} max={10} value={val} onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))} className="apexo-modal-input" />
                                                        ) : (
                                                            <input value={val} onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))} placeholder="Answer" className="apexo-modal-input" />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', marginTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                            <Button variant="ghost" onClick={() => setShowModal(false)}>Cancel</Button>
                            <Button variant="outline" onClick={handleSaveUpdate} isLoading={savingQa}>Save Draft</Button>
                            <Button variant="primary" onClick={handleCompleteFollowup} isLoading={savingQa}>✓ Complete Followup</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
